import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { BackButton, Button, EmptyState, LoadingState, Typography } from '../components/common';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { navigateBack } from '../utils/navigationUtils';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { ClubLeaguePlayerAnalysis } from '../components/clubs/dashboard/ClubLeaguePlayerAnalysis';
import {
  LeagueSeasonSelect,
  MatchCardModal,
} from '../components/leagues/LeagueMatchViews';
import {
  fetchClubLeagueLink,
  fetchLinkedClubSeason,
  fetchMatchCard,
  fetchMiddlesexSeasons,
} from '../services/leagueService';
import { pickDefaultClubLeagueTeam } from '../utils/leagueTeamSelection';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';

const ClubLeaguePlayerPerformancePage = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    club: selectedClub,
    role: userClubRole,
    loading: clubLoading,
    hasPermission,
    error: clubError,
  } = useClubPermissions(clubId, currentUser);

  const canManage = ['owner', 'admin', 'host'].includes(userClubRole)
    || currentUser?.role === 'admin'
    || currentUser?.role === 'super_admin';

  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [startYear, setStartYear] = useState(null);
  const [teamChoice, setTeamChoice] = useState(null);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    if (!clubId) return undefined;
    let cancelled = false;
    setLinkLoading(true);
    fetchClubLeagueLink(clubId)
      .then((next) => {
        if (!cancelled) setLink(next);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLinkLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  useEffect(() => {
    if (link?.status !== 'approved') {
      setSnapshot(null);
      setSeasons([]);
      setStartYear(null);
      return undefined;
    }
    let cancelled = false;
    fetchMiddlesexSeasons()
      .then((rows) => {
        if (cancelled) return;
        setSeasons(rows);
        setStartYear((prev) => (rows.some((row) => row.start_year === prev) ? prev : rows[0]?.start_year ?? null));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [link]);

  useEffect(() => {
    if (link?.status !== 'approved' || startYear == null) return undefined;
    let cancelled = false;
    fetchLinkedClubSeason(link.league_club_id, startYear)
      .then((season) => {
        if (!cancelled) setSnapshot(season);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [link, startYear]);

  const teamsById = useMemo(
    () => new Map((snapshot?.allTeams ?? []).map((team) => [team.id, team])),
    [snapshot],
  );
  const clubTeams = snapshot?.teams ?? [];
  const team = useMemo(() => pickDefaultClubLeagueTeam({
    teams: clubTeams,
    divisions: snapshot?.divisions,
    competitions: snapshot?.competitions,
    previousTeam: teamChoice,
  }), [clubTeams, snapshot, teamChoice]);
  const division = (snapshot?.divisions ?? []).find((row) => row.id === team?.division_id) ?? null;
  const competition = (snapshot?.competitions ?? []).find((row) => row.id === division?.competition_id) ?? null;
  const meta = [competition?.label, division?.name].filter(Boolean).join(' · ');

  const openFixture = async (fixture) => {
    if (!team) return;
    setSelectedFixture({ fixture, teamId: team.id });
    try {
      setSelectedCard(await fetchMatchCard(fixture.id));
    } catch {
      setSelectedCard({ lineups: [], rubbers: [], players: [] });
    }
  };

  if (clubLoading) {
    return (
      <div className={`${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`}>
        <div className={`mx-auto max-w-app-shell ${APP_PAGE_GUTTER} py-12`}>
          <LoadingState message="Loading player performance…" />
        </div>
      </div>
    );
  }

  if (clubError || !hasPermission || !canManage) {
    return (
      <div className={`${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`}>
        <div className={`mx-auto max-w-app-shell ${APP_PAGE_GUTTER} py-12`}>
          <Card variant="default" size="large" className="text-center py-12">
            <Heading level={2} className="mb-4">Access Denied</Heading>
            <Text className="text-text-secondary">
              {clubError || 'Only club hosts, admins, and owners can review league player performance.'}
            </Text>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`}>
      <div className={`mx-auto max-w-app-shell ${APP_PAGE_GUTTER} pt-3 pb-8 md:pt-6`}>
        <div className="mb-6">
          <BackButton
            onClick={() => navigateBack(navigate, location, clubId, `/club-management/${clubId}`)}
          >
            Back to Club Management
          </BackButton>
          <Heading level={1} className="mt-3 text-xl font-bold text-text-primary sm:text-2xl">
            Player performance
          </Heading>
          <Text className="mt-1 text-sm text-text-secondary">
            {selectedClub?.name
              ? `Review rubber records for ${selectedClub.name} county teams`
              : 'Review rubber records for this club’s county teams'}
          </Text>
        </div>

        {error ? <Typography variant="warning">{error}</Typography> : null}

        {linkLoading ? (
          <LoadingState message="Loading player performance…" />
        ) : !link || link.status !== 'approved' ? (
          <EmptyState
            title={link?.status === 'pending' ? 'Link pending approval' : 'No county club linked'}
            description="Request a Middlesex club link from League squad first."
            action={(
              <Button
                variant="primary"
                size="small"
                onClick={() => navigate(`/club/${clubId}/league-squad`, { state: location.state })}
              >
                Open League squad
              </Button>
            )}
          />
        ) : !snapshot ? (
          <LoadingState message="Loading linked teams…" />
        ) : !team ? (
          <EmptyState title="No teams this season" description="This club has no Middlesex teams in the selected season." />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <LeagueSeasonSelect
                id="league-performance-season-select"
                seasons={seasons}
                value={startYear}
                onChange={setStartYear}
                className="w-full sm:w-44"
              />
              {clubTeams.length > 1 ? (
                <>
                  <label htmlFor="league-performance-team-select" className="sr-only">Team</label>
                  <select
                    id="league-performance-team-select"
                    value={team.id}
                    onChange={(event) => {
                      const next = clubTeams.find((item) => item.id === event.target.value);
                      setTeamChoice(next
                        ? { id: next.id, name: next.name, source_team_id: next.source_team_id }
                        : null);
                    }}
                    className="form-input w-full sm:flex-1 sm:max-w-md min-h-[44px]"
                  >
                    {clubTeams.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <Typography className="text-sm font-semibold text-text-primary">{team.name}</Typography>
              )}
            </div>
            {meta ? (
              <Typography variant="caption" className="text-text-muted">{meta}</Typography>
            ) : null}

            {snapshot.season?.id ? (
              <ClubLeaguePlayerAnalysis
                clubId={clubId}
                currentUserId={currentUser?.id}
                teamId={team.id}
                seasonId={snapshot.season.id}
                teamsById={teamsById}
                onOpenFixture={openFixture}
                canSearchPlayers
                title="Player performance"
              />
            ) : null}
          </div>
        )}

        {selectedFixture ? (
          <MatchCardModal
            fixture={selectedFixture.fixture}
            card={selectedCard}
            teamsById={teamsById}
            competitionLabel={
              (snapshot?.competitions ?? []).find((row) => row.id === selectedFixture.fixture.competition_id)?.label
              ?? competition?.label
            }
            divisionName={
              (snapshot?.divisions ?? []).find((row) => row.id === selectedFixture.fixture.division_id)?.name
              ?? division?.name
            }
            ourTeamId={selectedFixture.teamId}
            onClose={() => {
              setSelectedFixture(null);
              setSelectedCard(null);
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ClubLeaguePlayerPerformancePage;
